import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Lock, KeyRound, ArrowRight, Shield } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

export default function LoginPage() {
  const { user, login, mustResetPassword, completePasswordReset } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgotPw, setShowForgotPw] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotOldPw, setForgotOldPw] = useState('');
  const [forgotNewPw, setForgotNewPw] = useState('');
  const [forgotConfirmPw, setForgotConfirmPw] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showNewPw, setShowNewPw] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    if (user && !mustResetPassword) navigate('/', { replace: true });
  }, [user, mustResetPassword, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Please fill all fields'); return; }
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.must_reset_password) {
        setCurrentPw(password); setShowResetForm(true);
        toast.info('You must change your password before continuing');
      } else {
        toast.success('Welcome to Transsolution');
        navigate('/', { replace: true });
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Login failed'); }
    finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPw || newPw.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (newPw !== confirmPw) { toast.error('Passwords do not match'); return; }
    setResetting(true);
    try {
      await completePasswordReset(currentPw, newPw);
      toast.success('Password updated successfully');
      navigate('/', { replace: true });
    } catch (e) { toast.error(e.response?.data?.detail || 'Password reset failed'); }
    finally { setResetting(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail || !forgotOldPw || !forgotNewPw) { toast.error('Fill all fields'); return; }
    if (forgotNewPw !== forgotConfirmPw) { toast.error('Passwords do not match'); return; }
    if (forgotNewPw.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setForgotLoading(true);
    try {
      await authAPI.forgotPassword({ email: forgotEmail, old_password: forgotOldPw, new_password: forgotNewPw });
      toast.success('Password changed! Please login.');
      setShowForgotPw(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Password change failed'); }
    finally { setForgotLoading(false); }
  };

  if (showResetForm) {
    return (
      <div className="min-h-screen flex" data-testid="reset-password-page">
        <div className="hidden lg:flex lg:w-1/2 wisteria-gradient-bg relative overflow-hidden items-center justify-center">
          <div className="absolute top-20 left-[15%] w-16 h-16 rounded-2xl bg-white/10 animate-float border border-white/20" />
          <div className="absolute bottom-32 right-[20%] w-20 h-20 rounded-full bg-white/5 animate-float-slow border border-white/10" />
          <div className="relative z-10 px-16 max-w-xl text-center">
            <Shield className="w-14 h-14 text-white/80 mx-auto mb-6" />
            <h2 className="font-heading font-semibold text-white text-3xl mb-3 tracking-tight">Secure Your Account</h2>
            <p className="text-white/60 text-sm">Choose a strong password to protect your data</p>
          </div>
        </div>
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[hsl(var(--background))]">
          <div className="w-full max-w-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <Lock className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h2 className="font-heading font-semibold text-[hsl(var(--foreground))] text-2xl tracking-tight">Change Password</h2>
                <p className="text-gray-500 text-sm">Set a new secure password to continue</p>
              </div>
            </div>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="form-label">New Password</label>
                <div className="relative">
                  <input type={showNewPw ? 'text' : 'password'} value={newPw} onChange={(e) => setNewPw(e.target.value)}
                    placeholder="Enter new password (min 6 chars)" className="form-input pr-10" data-testid="new-password" />
                  <button type="button" onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-purple-500">
                    {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="form-label">Confirm New Password</label>
                <input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
                  placeholder="Confirm new password" className="form-input" data-testid="confirm-password" />
              </div>
              {newPw && confirmPw && newPw !== confirmPw && <p className="text-xs text-red-500">Passwords do not match</p>}
              <button type="submit" disabled={resetting} className="btn-primary w-full py-3 text-sm" data-testid="reset-password-btn">
                {resetting ? 'Updating...' : 'Update Password & Continue'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* LEFT — Animated gradient + floating shapes */}
      <div className="hidden lg:flex lg:w-[55%] wisteria-gradient-bg relative overflow-hidden flex-col">
        {/* Floating 3D shapes */}
        <div className="absolute top-[15%] left-[10%] w-20 h-20 rounded-2xl bg-white/10 backdrop-blur-sm animate-float border border-white/20" />
        <div className="absolute top-[40%] right-[12%] w-16 h-16 rounded-full bg-white/8 backdrop-blur-sm animate-float-slow border border-white/15" />
        <div className="absolute bottom-[20%] left-[25%] w-14 h-14 rounded-xl bg-white/10 backdrop-blur-sm animate-float-delay border border-white/20" />
        <div className="absolute top-[65%] right-[30%] w-24 h-24 rounded-3xl bg-white/5 animate-float border border-white/10" />
        <div className="absolute top-[10%] right-[35%] w-10 h-10 rounded-lg bg-white/15 animate-float-slow border border-white/20" />

        <div className="relative z-10 flex-1 flex flex-col justify-center px-12 xl:px-20">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-4 mb-10">
              <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
                <span className="font-heading font-black text-white text-xl">T</span>
              </div>
              <div>
                <h1 className="font-heading font-bold text-white text-3xl tracking-tight">Transsolution</h1>
                <p className="text-white/50 text-[10px] font-bold uppercase tracking-[0.15em]">Logistics Accounting ERP</p>
              </div>
            </div>

            <h2 className="font-heading font-medium text-white text-xl lg:text-2xl leading-snug mb-4 max-w-lg tracking-tight">
              Complete accounting engine built for Indian logistics companies
            </h2>
            <p className="text-white/50 text-sm leading-relaxed mb-10 max-w-md">
              Unified GST, TDS, Advance Payments, and double-entry ledgers &mdash; all in one platform.
            </p>

            {/* Feature pills */}
            <div className="flex flex-wrap gap-2">
              {['GST Ready', 'TDS Auto', 'Multi-Bank', 'FIFO Advances', 'Dark Mode', 'AI Assistant'].map((f, i) => (
                <span key={i} className="px-3 py-1.5 rounded-full bg-white/10 text-white/80 text-[11px] font-medium border border-white/15 backdrop-blur-sm">{f}</span>
              ))}
            </div>
          </motion.div>
        </div>

        <div className="relative z-10 px-12 xl:px-20 pb-6">
          <p className="text-white/20 text-[10px] tracking-[0.1em] uppercase">Powered by Transsolution Technologies</p>
        </div>
      </div>

      {/* RIGHT — Glass Login Card */}
      <div className="w-full lg:w-[45%] flex items-center justify-center p-8 bg-[hsl(var(--background))]">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
          className="w-full max-w-sm">
          {/* Mobile header */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-600 to-purple-400 flex items-center justify-center">
              <span className="font-heading font-black text-white text-sm">T</span>
            </div>
            <h1 className="font-heading font-bold text-gray-800 dark:text-gray-100 text-xl tracking-tight">Transsolution</h1>
          </div>

          <div className="mb-8">
            <h2 className="font-heading font-semibold text-[hsl(var(--foreground))] text-2xl tracking-tight mb-1">Welcome back</h2>
            <p className="text-gray-500 text-sm">Sign in to access your accounting dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="form-label">Email or Username</label>
              <input type="text" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@vaishnavi.com"
                className="form-input" autoComplete="email" data-testid="login-email" />
            </div>
            <div>
              <label className="form-label">Password</label>
              <div className="relative">
                <input type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password" className="form-input pr-10" autoComplete="current-password" data-testid="login-password" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-purple-500 transition">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="login-submit"
              className="btn-primary w-full py-3 text-sm flex items-center justify-center gap-2">
              {loading ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <>Sign In <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <button onClick={() => { setShowForgotPw(true); setForgotEmail(email); }}
            className="w-full text-center text-sm text-gray-400 hover:text-purple-600 mt-4 transition-colors" data-testid="forgot-password-link">
            Forgot Password?
          </button>

          {/* Quick Stats */}
          <div className="mt-10 pt-6 border-t border-purple-100 dark:border-purple-900/30">
            <p className="text-[9px] text-purple-400 uppercase font-bold tracking-[0.15em] mb-3">Platform Highlights</p>
            <div className="grid grid-cols-2 gap-2.5">
              {[
                { v: 'GST Ready', l: 'GSTR-1, 3B, TDS' },
                { v: 'Dark Mode', l: 'Premium UI' },
                { v: 'Multi-Bank', l: 'Auto-Sync' },
                { v: 'FIFO', l: 'Advance Adjust' },
              ].map((s, i) => (
                <div key={i} className="glass-card rounded-lg p-2.5">
                  <p className="text-xs font-bold text-gray-800 dark:text-gray-200">{s.v}</p>
                  <p className="text-[10px] text-gray-400">{s.l}</p>
                </div>
              ))}
            </div>
          </div>

          <p className="text-center text-[9px] text-gray-400 mt-6 uppercase tracking-[0.1em]">Transsolution ERP v2.0</p>
        </motion.div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPw && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setShowForgotPw(false); }}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="glass-card rounded-2xl max-w-md w-full p-6" data-testid="forgot-password-modal">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <KeyRound className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg tracking-tight text-[hsl(var(--foreground))]">Reset Password</h3>
                <p className="text-[10px] text-gray-500">Verify your old password to set a new one</p>
              </div>
            </div>
            <form onSubmit={handleForgotPassword} className="space-y-3">
              <div><label className="form-label">Email or Username</label>
                <input type="text" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="Your email" className="form-input" data-testid="forgot-email" /></div>
              <div><label className="form-label">Current Password</label>
                <input type="password" value={forgotOldPw} onChange={e => setForgotOldPw(e.target.value)} placeholder="Enter your current password" className="form-input" data-testid="forgot-old-password" /></div>
              <div><label className="form-label">New Password</label>
                <input type="password" value={forgotNewPw} onChange={e => setForgotNewPw(e.target.value)} placeholder="Min 6 characters" className="form-input" data-testid="forgot-new-password" /></div>
              <div><label className="form-label">Confirm New Password</label>
                <input type="password" value={forgotConfirmPw} onChange={e => setForgotConfirmPw(e.target.value)} placeholder="Re-enter new password" className="form-input" data-testid="forgot-confirm-password" /></div>
              {forgotNewPw && forgotConfirmPw && forgotNewPw !== forgotConfirmPw && <p className="text-xs text-red-500">Passwords do not match</p>}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForgotPw(false)} className="btn-secondary flex-1 py-2.5">Cancel</button>
                <button type="submit" disabled={forgotLoading} className="btn-primary flex-1 py-2.5" data-testid="forgot-submit">
                  {forgotLoading ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
